import {
  tryFetchTransactions,
  tryVerifyAddress,
  tryFetchBalance,
  tryFetchFee,
} from '@/core/presenter/controller/HomeController';
import { HomeViewType } from '@/core/presenter/viewModel';
import { useStores } from '@/stores';
import dayjs from 'dayjs';
import { useState, useEffect, useCallback } from 'react';
import * as ViewModel from '@/core/presenter/viewModel';
import { showAlertPopup, showConfirmPopup } from '@/core/presenter/controller/PopupController';
import { BUTTON_MSG, INPUT_MSG } from '@/consts/message';
import { RouterPath } from '@/routes/RouterPath';
import { useNavigate, useLocation } from 'react-router-dom';
import { checkSessionAndRedirect } from '@/core/presenter/controller/LoginController';
import { replaceNewLineToTag } from '@/utils';
import jsQR from 'jsqr';
import { MAX_MEMO_LENGTH } from '@/consts';

export default function useHome() {
  const { presentationStore, userStore } = useStores();
  const [viewType, setViewType] = useState<HomeViewType>(HomeViewType.MAIN);
  const [sendAddress, setSendAddress] = useState<string>('');
  const [sendAsset, setSendAsset] = useState<string>('');
  const [selectedAsset, setSelectedAsset] = useState<ViewModel.IAsset | undefined>(userStore.myAsset?.tokenAsset);
  const [isFeeNotice, setIsFeeNotice] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(ViewModel.HomeTabType.ASSETS);
  const [memo, setMemo] = useState<string>('');
  const activities = [...presentationStore.transactions].sort((a, b) => dayjs(b.date).unix() - dayjs(a.date).unix());
  const isNotActivities = activities.length < 1;
  const isWritting = sendAddress.length > 0;
  const isSendingAsset = (presentationStore.balance?.tokenBalance ?? 0) > 0;
  const defaultMemo = presentationStore.transactions.filter((item) => item.to === sendAddress)[0]?.to_memo;
  const curculateKrw = (
    selectedAsset?.tokenPrice
      ? selectedAsset?.tokenPrice * Number(sendAsset.replace(/,/g, ''))
      : selectedAsset?.trxPrice
        ? selectedAsset?.trxPrice * Number(sendAsset.replace(/,/g, ''))
        : 0
  ).toLocaleString();
  const isSendCheckBtnDisabled = sendAsset.length < 1;
  const fee = presentationStore.fee ?? 1;
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as {
    isSendView?: boolean;
    sendData?: {
      address: string;
      asset: string;
      selectedAsset?: ViewModel.IAsset;
      assetSymbol?: string;
    };
    userId?: string;
    userPhone?: string;
    from?: string;
    to?: string;
    amount?: number;
    memo?: string;
    viewType?: HomeViewType;
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [viewType]);

  useEffect(() => {
    const checkAuth = async () => {
      await checkSessionAndRedirect();
    };
    checkAuth();
  }, []);

  useEffect(() => {
    const initializeData = async () => {
      try {
        if (userStore.user?.trxAddress) {
          await Promise.all([
            activeTab === ViewModel.HomeTabType.ACTIVITIES ||
            viewType === HomeViewType.TRX ||
            viewType === HomeViewType.TOKEN
              ? tryFetchTransactions(userStore.user.trxAddress)
              : Promise.resolve(),
            activeTab === ViewModel.HomeTabType.ASSETS && viewType === ViewModel.HomeViewType.MAIN
              ? tryFetchBalance(userStore.user.trxAddress)
              : Promise.resolve(),
          ]);
        }
      } catch (error) {
        console.error('데이터 로드 실패:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeData();
  }, [selectedAsset?.symbol, sendAsset, userStore.user?.trxAddress, activeTab, viewType]);

  useEffect(() => {
    setSelectedAsset(userStore.myAsset?.tokenAsset ?? userStore.user?.tokenAsset);
  }, [userStore.myAsset?.tokenAsset, userStore.user?.tokenAsset]);

  useEffect(() => {
    const handleSendView = async () => {
      if (state?.isSendView) {
        setViewType(HomeViewType.SEND_CHECK);
        const isOk = await showConfirmPopup(BUTTON_MSG.SEND_COMPLETE);
        if (!isOk) return;
        setViewType(HomeViewType.MAIN);
      }
    };

    handleSendView();
  }, [state]);

  useEffect(() => {
    if (state?.viewType === HomeViewType.SEND) {
      setViewType(HomeViewType.SEND);
      if (state.sendData) {
        setSendAddress(state.sendData.address);
      }
    }
  }, [state, userStore.myAsset]);

  /** trx 주소 복사 */
  const onClickCopyBtn = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();

      const text = userStore.user?.trxAddress ?? '';

      navigator.clipboard.writeText(text);
    },
    [userStore.user?.trxAddress],
  );

  /** 잔액 재조회 */
  const onClickResetBtn = useCallback(() => {
    if (viewType === HomeViewType.MAIN) {
      tryFetchTransactions(userStore.user?.trxAddress ?? '');
      tryFetchBalance(userStore.user?.trxAddress ?? '');
    }
    if (viewType === HomeViewType.TRX) {
      tryFetchTransactions(userStore.user?.trxAddress ?? '');
      tryFetchBalance(userStore.user?.trxAddress ?? '');
    }
    if (viewType === HomeViewType.TOKEN) {
      tryFetchTransactions(userStore.user?.trxAddress ?? '');
      tryFetchBalance(userStore.user?.trxAddress ?? '');
    }
  }, [userStore.user?.trxAddress, viewType]);

  /** 뷰 타입 변경 이벤트 */
  const onClickViewType = useCallback((viewType: HomeViewType) => {
    setViewType(viewType);
    window.scrollTo(0, 0);
  }, []);

  /** 뒤로가기 이벤트 */
  const onClickBackBtn = useCallback(() => {
    if (viewType === HomeViewType.SEND) {
      setSendAddress('');
      setSendAsset('');
    }
    setViewType(HomeViewType.MAIN);
  }, [viewType]);

  /** 보내기 이벤트 */
  const onClickSendBtn = useCallback(() => {
    setViewType(HomeViewType.SEND);
    setSelectedAsset(userStore.myAsset?.tokenAsset);
  }, [userStore.myAsset]);

  /** input - sendAddress 경 이벤트 */
  const onChangeSendAddress = useCallback((e: React.SyntheticEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    setSendAddress(value);
  }, []);

  /** input - sendAsset 변경 이벤트 */
  const onChangeSelectedAsset = useCallback((e: React.SyntheticEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    // 소수점 4째자리까지만 허용하는 정규 표현식
    const regex = /^\d*\.?\d{0,4}$/;
    if (regex.test(value)) {
      setSendAsset(value);
    } else {
      console.error('Invalid input: Only up to 4 decimal places are allowed.');
    }
  }, []);

  const onKeyPress = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    const regex = /^[0-9]*\.?[0-9]{0,4}$/; // 소수점 4째자리까지만 허용하는 정규 표현식
    const currentValue = (e.target as HTMLInputElement).value + e.key;
    if (!regex.test(currentValue)) {
      e.preventDefault();
    }
  }, []);

  /** 주소 삭제 이벤트 */
  const onClickDeleteBtn = useCallback(() => {
    setSendAddress('');
    setSendAsset('');
    setSelectedAsset(userStore.myAsset?.tokenAsset);
  }, [userStore.myAsset?.tokenAsset]);

  /** 금액 초기화 이벤트 */
  const onClickResetAmountBtn = useCallback(() => setSendAsset(''), []);

  /** 붙여넣기 이벤트 */
  const onPaste = useCallback((text: string) => setSendAddress(text), []);

  /** 선택한 자산 변경 이벤트 */
  const onChangeSelectedAssetValue = useCallback(
    (assetType: ViewModel.AssetType) => {
      const newAsset =
        assetType === ViewModel.AssetType.TRX ? userStore.myAsset?.trxAsset : userStore.myAsset?.tokenAsset;
      setSelectedAsset(newAsset);
      setSendAsset('');
    },
    [userStore.myAsset],
  );

  /** 보내기 다음 버튼 체크 이벤트 */
  const onClickSendCheckBtn = useCallback(async () => {
    const isSuccessVerifyAddress = await tryVerifyAddress(sendAddress);

    if (!isSuccessVerifyAddress.succed) {
      showConfirmPopup(BUTTON_MSG.INVALID_ADDRESS);
      return;
    }

    if ((selectedAsset?.balance ?? 0) < Number(sendAsset)) {
      const isOk = await showConfirmPopup(BUTTON_MSG.BALANCE_NOT_ENOUGH);
      if (!isOk) return;

      setSendAsset('');
      return;
    }
    tryFetchFee(selectedAsset?.symbol ?? '', userStore.user?.trxAddress ?? '', Number(sendAsset.replace(/,/g, '')));
    setViewType(HomeViewType.SEND_CHECK);
  }, [selectedAsset?.balance, selectedAsset?.symbol, sendAddress, sendAsset, userStore.user?.trxAddress]);

  /** 보내기 취소 이벤트 */
  const onClickSendCancelBtn = useCallback(async () => {
    const isOk = await showAlertPopup(BUTTON_MSG.SEND_CANCEL, false);
    if (!isOk) return;
    setViewType(HomeViewType.SEND);
    setSendAddress('');
  }, []);

  /** 보내기 확인 화면에서 뒤로가기 이벤트 */
  const onClickBacktoSendView = useCallback(() => setViewType(HomeViewType.SEND), []);
  /** 보내기 확인 화면에서 다음 버튼 이벤트 */
  const onClickSendCheckNextBtn = useCallback(() => {
    if ((selectedAsset?.balance ?? 0) - fee < 0 || (presentationStore.balance?.trxBalance ?? 0) < fee) {
      setIsFeeNotice(true);
      showAlertPopup(replaceNewLineToTag(INPUT_MSG.CHECK_FEE_FAILED), true);
    } else {
      navigate(RouterPath.PIN, {
        state: {
          type: 'send_verification',
          returnPath: RouterPath.HOME,
          isSendView: true,
          sendData: {
            address: sendAddress,
            asset: sendAsset,
            assetSymbol: selectedAsset?.symbol,
            assetName: selectedAsset?.name,
          },
          userId: userStore.user?.id,
          userPhone: userStore.user?.phone,
          from: userStore.user?.trxAddress,
          to: sendAddress,
          amount: Number(sendAsset),
          memo: memo.length < 1 ? defaultMemo : memo,
        },
        replace: true,
      });
    }
  }, [
    presentationStore.balance?.trxBalance,
    selectedAsset?.balance,
    selectedAsset?.symbol,
    selectedAsset?.name,
    fee,
    sendAddress,
    sendAsset,
    userStore.user?.id,
    userStore.user?.phone,
    userStore.user?.trxAddress,
    memo,
    defaultMemo,
    navigate,
  ]);

  /** 메인 뷰로 이동 이벤트 */
  const onMoveMainView = useCallback(() => {
    setViewType(HomeViewType.MAIN);
  }, []);

  /** 카메라 이벤트 */
  const onClickCameraBtn = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });

      // 비디오 요소 생성
      const video = document.createElement('video');
      video.srcObject = stream;
      video.style.position = 'fixed';
      video.style.top = '0';
      video.style.left = '0';
      video.style.width = '100vw';
      video.style.height = '100vh';
      video.style.objectFit = 'cover';
      video.style.zIndex = '9999';
      video.style.backgroundColor = '#000';

      // Canvas 요소 생성 (QR 스캔용)
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      // 전체 화면을 감싸는 컨테이너 생성
      const container = document.createElement('div');
      container.style.position = 'fixed';
      container.style.top = '0';
      container.style.left = '0';
      container.style.width = '100vw';
      container.style.height = '100vh';
      container.style.zIndex = '9999';
      container.style.cursor = 'pointer';

      // QR 스캔 프레임 생성
      const scanFrame = document.createElement('div');
      scanFrame.style.position = 'fixed';
      scanFrame.style.top = '50%';
      scanFrame.style.left = '50%';
      scanFrame.style.transform = 'translate(-50%, -50%)';
      scanFrame.style.width = '280px';
      scanFrame.style.height = '280px';
      scanFrame.style.border = '2px solid #fff';
      scanFrame.style.zIndex = '9999';
      scanFrame.style.pointerEvents = 'none'; // 프레임을 클릭해도 container의 클릭 이벤트가 발생하도록

      // 닫기 함수
      const closeCamera = () => {
        document.body.removeChild(container);
        stream.getTracks().forEach((track) => track.stop());
        clearInterval(scanInterval);
      };

      // 컨테이너 클릭 이벤트
      container.onclick = closeCamera;

      // QR 코드 스캔 함수
      const scanQRCode = () => {
        if (video.readyState === video.HAVE_ENOUGH_DATA && context) {
          canvas.height = video.videoHeight;
          canvas.width = video.videoWidth;
          context.drawImage(video, 0, 0, canvas.width, canvas.height);
          const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
          const code = jsQR(imageData.data, imageData.width, imageData.height);

          if (code) {
            setSendAddress(code.data);
            closeCamera();
          }
        }
      };

      // 주기적으로 QR 스캔
      const scanInterval = setInterval(scanQRCode, 100);

      // 요소들을 컨테이너에 추가
      container.appendChild(video);
      container.appendChild(scanFrame);
      document.body.appendChild(container);
      video.play();
    } catch (err) {
      console.error('카메라 접근 실패:', err);
      alert('카메라 접근에 실패했습니다.');
    }
  }, [setSendAddress]);

  /** 선택한 자산 초기화 이벤트 */
  const onResetSelectedAsset = useCallback(() => {
    setSelectedAsset(userStore.myAsset?.tokenAsset);
  }, [userStore.myAsset]);

  /** 홈 탭 이벤트 */
  const onClickActiveTab = useCallback((tab: ViewModel.HomeTabType) => setActiveTab(tab), []);

  /** 이전 거래 보기 이벤트 */
  const onClickPrevTransactionBtn = useCallback(() => {
    window.open(`${import.meta.env.VITE_TRON_EXPLORER_URL}#/address/${userStore.user?.trxAddress}`, '_blank');
  }, [userStore.user?.trxAddress]);

  /** 메모 변경 이벤트 */
  const onChangeMemo = useCallback(
    (e: React.SyntheticEvent<HTMLInputElement>) => {
      const value = e.currentTarget.value;
      if (memo.length === 0) setMemo('');
      if (value.length <= MAX_MEMO_LENGTH) setMemo(value);
      if (value.length < 1 && defaultMemo.length !== 0) setMemo(defaultMemo);
    },
    [defaultMemo, memo.length],
  );

  const resetSendInfo = useCallback(() => {
    setSendAsset('');
    setSendAddress('');
  }, []);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handlePopState = (event: PopStateEvent) => {
      if (viewType === HomeViewType.SEND_CHECK) {
        setViewType(HomeViewType.MAIN); // 메인으로 이동
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [viewType]);

  return {
    isLoading,
    viewType,
    activities,
    isNotActivities,
    isWritting,
    sendAddress,
    sendAsset,
    curculateKrw,
    selectedAsset,
    trxAsset: userStore.user?.trxAsset,
    tokenAsset: userStore.user?.tokenAsset,
    totalBalance:
      (Number(presentationStore.balance?.tokenKRW) + Number(presentationStore.balance?.trxKRW)).toLocaleString() ?? '',
    trxAddress: userStore.user?.trxAddress ?? '',
    trxTransactions: presentationStore.transactions.filter((item) => item.coinType === ViewModel.AssetType.TRX),
    tokenTransactions: presentationStore.transactions.filter((item) => item.coinType === ViewModel.AssetType.DBS),
    trxBalance: (presentationStore.balance?.trxBalance ?? 0).toLocaleString() ?? 0,
    trxKrw: Number(presentationStore.balance?.trxKRW ?? 0).toLocaleString() ?? 0,
    tokenBalance: (presentationStore.balance?.tokenBalance ?? 0).toLocaleString() ?? 0,
    tokenKrw: Number(presentationStore.balance?.tokenKRW).toLocaleString() ?? 0,
    tokenPrice: presentationStore.balance?.tokenPrice ?? 0,
    trxPrice: presentationStore.balance?.trxPrice ?? 0,
    isSendCheckBtnDisabled,
    isFeeNotice,
    fee,
    coinType: presentationStore.transactions[0]?.coinType ?? '',
    activeTab,
    defaultMemo,
    isSendingAsset,
    dbsBuyCount: Number(presentationStore.balance?.dbsBuyCount ?? 0).toLocaleString(),
    dbsBuyCountKRW: Number(presentationStore.balance?.dbsBuyCountKRW ?? 0).toLocaleString(),
    onClickActiveTab,
    onClickCopyBtn,
    onClickResetBtn,
    onClickViewType,
    onClickSendBtn,
    onClickBackBtn,
    onChangeSendAddress,
    onChangeSelectedAsset,
    onChangeSelectedAssetValue,
    onClickDeleteBtn,
    onClickResetAmountBtn,
    onPaste,
    onClickSendCheckBtn,
    onClickSendCancelBtn,
    onClickBacktoSendView,
    onClickSendCheckNextBtn,
    onMoveMainView,
    onClickCameraBtn,
    onResetSelectedAsset,
    onClickPrevTransactionBtn,
    onChangeMemo,
    onKeyPress,
    resetSendInfo,
  };
}
